import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, MapPin, Clock, ShieldCheck, MessageSquare, PiggyBank, Fingerprint, Headphones, Star, Plus, Minus, ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import serviceBg from "@/assets/service-bg.webp";
import armandsHeadshot from "@/assets/armands-headshot.jpg";
import armandsFaq from "@/assets/armands-faq.png";
import expertiseBg from "@/assets/expertise-bg.png";
import worldMapBg from "@/assets/world-map-bg.png";
import voipCtaBg from "@/assets/voip-cta-bg.webp";
import type { City } from "@/data/cities-types";
import { getPostsByLocation } from "@/data/blog-posts";

export type LocationVariant = {
  heroHeading?: ReactNode;
  serviceLabel?: string;
  serviceLabelShort?: string;

  parentHref?: string;
  parentLabel?: string;
  learnMoreText?: string;
  nearMeEyebrow?: string;
  whyTitle?: string;
  expertHeroTitle?: string;
  expertNearMeTitle?: string;
  introStrong?: string;
  introBody?: ReactNode;
  faqHeading?: ReactNode;
  ctaTitle?: string;
  ctaBody?: ReactNode;
  blogEyebrow?: string;
  blogTitle?: string;
  faqs?: { q: string; a: string }[];
};

type Props = { city: City; variant?: LocationVariant };

type Review = {
  name: string;
  date: string;
  initials: string;
  initialsBg: string;
  text: string;
};

const REVIEWS: Review[] = [
  {
    name: "DynaClean Professional Services, LLC",
    date: "1 April 2025",
    initials: "DC",
    initialsBg: "bg-red-500",
    text: "Called Simple Tech today for a hard ware issue. Armands answered the call. He asked how he can help. I shared that I was looking for a power cord for an older external drive. He asked a few questions, asked for a picture of the device. Before i knew it, he sent me a couple links to solve my issue. Then he shared they are a Teck company and not a hardware retailer. He helped me find what I was looking for! Thank you Armolds!!! 🤗😀😃😄☺ Five Smiles!!!",
  },
  {
    name: "Jeff Konieczko",
    date: "29 January 2025",
    initials: "JK",
    initialsBg: "bg-slate-500",
    text: "I had an excellent experience working with Armand Williams for my IT needs. He provides outstanding customer service, always prioritizing the needs of his clients. When I needed urgent assistance reconfiguring my Teams app for an important presentation, Armand was readily available and quickly resolved the issue. His expertise, professionalism, and dedication to solving IT challenges make him a valuable resource. I highly recommend his services to anyone in need of reliable and efficient IT support!",
  },
  {
    name: "Christina Thompson",
    date: "22 July 2024",
    initials: "CT",
    initialsBg: "bg-amber-500",
    text: "I used SIMPLETECH to set up my office phones for my second location. I was having a hard time setting them up myself and Armands made it seamless. Highly recommend!",
  },
  {
    name: "Camille Leon",
    date: "5 July 2024",
    initials: "CL",
    initialsBg: "bg-emerald-500",
    text: "Since hiring SimpleTech to handle social media posts for the Holistic Chamber of Commerce (HCC), it has been a game changer for our brand. Professional, responsive, and creative!",
  },
];


export function LocationPageTemplate({ city, variant }: Props) {
  const v = {
    serviceLabel: variant?.serviceLabel ?? "Managed IT Services",
    serviceLabelShort: variant?.serviceLabelShort ?? "Managed IT",
    parentHref: variant?.parentHref ?? "/services/managed-it-services",
    parentLabel: variant?.parentLabel ?? "Managed IT Services",
    learnMoreText: variant?.learnMoreText ?? "LEARN MORE ABOUT MANAGED IT",
    nearMeEyebrow: variant?.nearMeEyebrow ?? "Managed IT Services Near Me",
    whyTitle: variant?.whyTitle ?? "Why Choose Our Managed IT Services?",
    expertHeroTitle:
      variant?.expertHeroTitle ?? `Expert Managed IT Services ${city.name} Businesses rely on`,
    expertNearMeTitle:
      variant?.expertNearMeTitle ?? `Expert Managed IT Services in ${city.name}, ${city.region}`,
    introStrong: variant?.introStrong ?? `managed IT services ${city.name}`,
    ctaTitle: variant?.ctaTitle ?? "Limited-Time Offer: Free Managed IT Consultation!",
    blogEyebrow: variant?.blogEyebrow ?? "From the Blog",
    blogTitle: variant?.blogTitle ?? `IT Insights for ${city.name} Businesses`,
  };
  const [reviewPage, setReviewPage] = useState(0);
  const pageCount = Math.ceil(REVIEWS.length / 2);
  const visibleReviews = REVIEWS.slice(reviewPage * 2, reviewPage * 2 + 2);
  const goPrev = () => setReviewPage((p) => (p - 1 + pageCount) % pageCount);
  const goNext = () => setReviewPage((p) => (p + 1) % pageCount);
  const cityPosts = getPostsByLocation(city.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="services" />

      <main id="main-content" className="flex-1">
        {/* PAGE BANNER */}
        <section
          className="relative bg-[#0b0a3d] bg-cover bg-center text-white"
          style={{ backgroundImage: `url(${serviceBg})` }}
        >
          <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <h1
              className={`text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight leading-[1.1] ${
                variant?.heroHeading ? "" : "whitespace-nowrap"
              }`}
            >
              {variant?.heroHeading ?? <>{v.serviceLabel} {city.name}</>}
            </h1>

            <nav
              aria-label="Breadcrumb"
              className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs md:text-sm font-bold tracking-wider uppercase text-white/80"
            >
              <Link to="/" className="hover:text-sky-400">Home</Link>
              <ChevronRight className="w-4 h-4 text-sky-400" />
              <Link to="/services" className="hover:text-sky-400">Services</Link>
              <ChevronRight className="w-4 h-4 text-sky-400" />
              <a href={v.parentHref} className="hover:text-sky-400">
                {v.parentLabel}
              </a>
              <ChevronRight className="w-4 h-4 text-sky-400" />
              <span className="text-white">{v.serviceLabel} {city.name}</span>
            </nav>
          </div>
        </section>

        {/* EXPERTISE */}
        <section className="relative bg-white py-20 md:py-28 overflow-hidden">
          <img
            src={expertiseBg}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover opacity-80"
          />
          <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            <div>
              <p className="text-xs font-bold tracking-widest text-indigo-600 uppercase">
                Expertise
              </p>
              <h2 className="mt-4 text-4xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight leading-[1.1]">
                {v.expertHeroTitle}
              </h2>
              {variant?.introBody ? (
                <div className="mt-8 space-y-6 text-base md:text-lg text-slate-600 leading-relaxed">
                  {variant.introBody}
                </div>
              ) : (
                <>
                  <p className="mt-8 text-base md:text-lg text-slate-600 leading-relaxed">
                    At SimpleTech, we provide{" "}
                    <strong className="text-[#0b0a3d]">{v.introStrong}</strong>{" "}
                    businesses rely on to stay secure, connected, and efficient. Whether you're
                    running a small office or a multi-site operation, we ensure your technology runs
                    smoothly—so you can focus on growing your business.
                  </p>
                  <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
                    Located right here at{" "}
                    <strong className="text-[#0b0a3d]">
                      8880 N Benson Ave #121, Montclair, CA 91763
                    </strong>
                    , we proudly serve {city.name} and nearby areas like Upland, Claremont, Rancho
                    Cucamonga, and Ontario. When you partner with us, you're choosing a local team
                    that understands your challenges and is dedicated to delivering real results.
                  </p>
                </>
              )}
              <div className="mt-10">
                <a
                  href={v.parentHref}
                  className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                >
                  {v.learnMoreText}
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src={armandsHeadshot}
                alt={`Armands Williams — SimpleTech owner serving ${city.name}`}
                className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </section>

        {/* SERVING MONTCLAIR & BEYOND */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-xs font-bold tracking-widest text-sky-600 uppercase">
              {v.nearMeEyebrow}
            </p>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight leading-[1.1]">
              Serving {city.name} &amp; Beyond
            </h2>
            <p className="mt-8 max-w-3xl mx-auto text-base md:text-lg text-slate-600 leading-relaxed">
              SimpleTech is more than just IT support — we help {city.name} businesses stay connected, secure, and efficient. Whether it's remote assistance, software integration, or building a smarter tech plan, we're just a call away. When on-site help is needed, being local means we respond fast. We proudly serve:
            </p>
            <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-8 max-w-4xl mx-auto text-left">
              {[
                { name: city.name, bold: true },
                { name: "Claremont", bold: false },
                { name: "Upland", bold: false },
                { name: "Ontario", bold: false },
                { name: "Rancho Cucamonga", bold: false },
                { name: "Other surrounding communities.", bold: true },
              ].map((item) => (
                <li key={item.name} className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span
                    className={
                      item.bold
                        ? "font-bold text-[#0b0a3d] text-base"
                        : "font-semibold text-slate-400 text-base"
                    }
                  >
                    {item.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section
          className="relative bg-[#0b0a3d] bg-cover bg-center text-white py-20 md:py-28 overflow-hidden"
          style={{ backgroundImage: `url(${serviceBg})` }}
        >
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.1] max-w-3xl mx-auto">
              {v.whyTitle}
            </h2>
            <p className="mt-8 max-w-3xl mx-auto text-base md:text-lg text-white/80 leading-relaxed">
              SimpleTech is your trusted IT partner right here in {city.name}. Local businesses rely on us for fast response times and hands-on support. Being just minutes away means when you need on-site help, we're there—fast. We proudly serve:
            </p>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12 text-left max-w-6xl mx-auto">
              {[
                { Icon: Clock, title: "24/7 Proactive Monitoring", desc: "Stop problems before they disrupt your business." },
                { Icon: ShieldCheck, title: "Secure, Compliant Systems", desc: "We safeguard your data with advanced cybersecurity." },
                { Icon: MessageSquare, title: "Personalized Local Service", desc: `Owner Armands & our ${city.name} team treat your business like it's our own.` },
                { Icon: PiggyBank, title: "Cost-Effective Support Plans", desc: "Predictable pricing helps you control IT budgets." },
                { Icon: Fingerprint, title: "Scalable Solutions", desc: "From 5 to 500 users, we tailor IT support to your growth." },
                { Icon: Headphones, title: "Rapid Response Guarantee", desc: "Get expert help within minutes when IT issues arise." },
              ].map(({ Icon, title, desc }) => (
                <div key={title} className="flex gap-5 items-start">
                  <Icon className="w-12 h-12 text-white flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <h3 className="text-lg md:text-xl font-extrabold">{title}</h3>
                    <p className="mt-3 text-white/80 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16">
              <Link
                to="/contact"
                className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                CONTACT US TODAY FOR A FREE CONSULTATION
              </Link>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section
          className="relative bg-white py-20 md:py-28 overflow-hidden"
          style={{
            backgroundImage: `url(${worldMapBg})`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        >
          <div className="relative max-w-7xl mx-auto px-6">
            <p className="text-center text-xs font-bold tracking-widest text-indigo-600 uppercase">
              {v.nearMeEyebrow}
            </p>
            <h2 className="mt-4 text-center text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight leading-[1.1]">
              {v.expertNearMeTitle}
            </h2>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
              {visibleReviews.map((r) => (
                <article
                  key={r.name + r.date}
                  className="bg-white rounded-2xl shadow-xl p-8 md:p-10"
                >
                  <header className="flex items-center gap-4">
                    <div
                      className={`w-14 h-14 rounded-full ${r.initialsBg} text-white font-bold flex items-center justify-center text-lg flex-shrink-0`}
                    >
                      {r.initials}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0b0a3d] text-base md:text-lg leading-tight">
                        {r.name}
                      </h3>
                      <p className="text-slate-500 text-sm mt-1">{r.date}</p>
                    </div>
                  </header>
                  <p className="mt-6 text-slate-700 text-base leading-relaxed">
                    {r.text}
                  </p>
                </article>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-center gap-4">
              <button
                onClick={goPrev}
                aria-label="Previous reviews"
                className="w-12 h-12 rounded-full border border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-700 shadow-sm transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-semibold text-slate-500 tabular-nums">
                {reviewPage + 1} / {pageCount}
              </span>
              <button
                onClick={goNext}
                aria-label="Next reviews"
                className="w-12 h-12 rounded-full border border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-700 shadow-sm transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-white pt-20 md:pt-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-end">
              <div className="flex flex-col h-full">
                <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">
                  {variant?.faqHeading ?? (
                    <>Frequently Asked<br />Questions About<br />{v.serviceLabel}</>
                  )}
                </h2>
                <div className="mt-10 flex-1 flex items-end">
                  <img
                    src={armandsFaq}
                    alt={`Armands Williams, SimpleTech owner answering common IT support questions for ${city.name} businesses`}
                    className="w-full max-w-md object-contain block"
                  />

                </div>
              </div>

              <FaqList cityName={city.name} faqs={variant?.faqs} />
            </div>
          </div>
        </section>

        {/* RELATED BLOG POSTS */}
        {cityPosts.length > 0 && (
          <section className="bg-white py-20 md:py-28">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                  <p className="text-xs font-bold tracking-widest text-indigo-600 uppercase">
                    {v.blogEyebrow}
                  </p>
                  <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight leading-[1.1]">
                    {v.blogTitle}
                  </h2>
                </div>
                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-sky-500 hover:text-sky-600"
                >
                  View All Posts <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {cityPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to="/articles/$slug"
                    params={{ slug: post.slug }}
                    className="group block rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-white border border-slate-100"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <p className="text-xs font-bold tracking-wider uppercase text-sky-600">
                        {post.category} · {post.date}
                      </p>
                      <h3 className="mt-3 text-lg font-extrabold text-[#0b0a3d] leading-snug group-hover:text-sky-500 transition-colors">
                        {post.title}
                      </h3>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Limited-Time VoIP Offer */}
        <section
          className="relative bg-[#0a0d3d] bg-cover bg-center py-20 md:py-28"
          style={{ backgroundImage: `url(${voipCtaBg})` }}
        >
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-white rounded-md shadow-2xl px-8 md:px-16 py-12 md:py-16 text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                {v.ctaTitle}
              </h2>
              {variant?.ctaBody ?? (
                <>
                  <p className="mt-6 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
                    For a limited time, {city.name} businesses get a{" "}
                    <strong className="font-bold">FREE 30-minute VoIP consultation</strong>
                    —valued at $199! Discover how a modern phone system can save money and boost efficiency. Offer valid through July 31, 2026.
                  </p>
                  <p className="mt-6 text-slate-700 text-base md:text-lg">
                    <strong className="font-bold">Call now:</strong>{" "}
                    <a href="tel:+19092559112" className="text-sky-700 underline hover:text-sky-800 transition-colors font-semibold whitespace-pre-line">
                      {"\n"}(909) 255-9112
                    </a>{" "}
                    or{" "}
                    <Link to="/contact" className="text-sky-700 underline hover:text-sky-800 transition-colors font-semibold">
                      Schedule your free consultation
                    </Link>
                    .
                  </p>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

const FAQS = (cityName: string) => [
  {
    q: "Q1: What are managed IT services?",
    a: "Managed IT services are ongoing technology support solutions where a third-party provider like SimpleTech handles your IT needs — including monitoring, security, updates, and support. For Montclair businesses, this means less downtime, fewer IT headaches, and access to expert help without needing an in-house team.",
  },
  {
    q: "Q2: How does SimpleTech's process work?",
    a: "We start with a free consultation right here in Montclair to assess your current IT setup. From there, we build a custom support plan that fits your business. Our local team monitors your systems 24/7, responds to issues quickly, and keeps your network secure and efficient.",
  },
  {
    q: "Q3: Which areas do you serve?",
    a: "We proudly serve businesses in Montclair, Upland, Ontario, Claremont, Rancho Cucamonga, and surrounding areas. Because we're based right here in Montclair, we can provide fast on-site service when needed.",
  },
  {
    q: "Q4: What makes you different from other IT companies?",
    a: "Unlike national providers, we're a locally owned Montclair IT company that offers personalized support. You'll speak with real people, not call centers, and get rapid response times from experts who understand your specific business environment.",
  },
  {
    q: "Q5: How do I schedule a consultation?",
    a: "It's easy — just call us at (909) 255-9112 or email contact@simpletechhelper.com. You can also stop by our office at 8880 N Benson Ave #121, Montclair, CA 91763.",
  },
];

function FaqList({ cityName, faqs }: { cityName: string; faqs?: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  const items = faqs ?? FAQS(cityName);
  return (
    <div className="divide-y divide-slate-200 lg:mt-32">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="py-5">
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              className="w-full flex items-start gap-4 text-left group"
            >
              <Star className="w-5 h-5 mt-1 flex-shrink-0 text-purple-600 fill-purple-600" />
              <span className="flex-1 text-lg md:text-xl font-semibold text-slate-900">
                {item.q}
              </span>
              {isOpen ? (
                <Minus className="w-5 h-5 mt-1 flex-shrink-0 text-purple-600" />
              ) : (
                <Plus className="w-5 h-5 mt-1 flex-shrink-0 text-purple-600" />
              )}
            </button>
            {isOpen && (
              <p className="mt-3 pl-9 pr-9 text-slate-600 leading-relaxed">
                {item.a}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
